'use client'

import { useEffect, useRef, useState } from 'react'
import { Globe, ChevronDown } from 'lucide-react'
import { Link } from '@/lib/i18n/navigation'

const LOCALES = ['ru', 'ro', 'en', 'uk']

// Тот же маппинг ярлыков, что и в десктопном LocaleSwitcher (LandingMarketingView.tsx)
const LOCALE_LABEL: Record<string, string> = { uk: 'UKR' }
function localeLabel(l: string): string {
  return LOCALE_LABEL[l] ?? l.toUpperCase()
}

/**
 * Компактный переключатель языка для мобильного хедера лендинга (< sm).
 *
 * Десктопный LocaleSwitcher — инлайн-ссылки, но на мобильном для них нет
 * места (и у хедера лендинга нет бургер-меню как fallback), из-за чего
 * до логина сменить язык с телефона было вообще нельзя. Здесь — кнопка с
 * текущим языком, раскрывающая короткий список остальных локалей.
 *
 * Использует тот же `Link ... locale={l}` из next-intl navigation, что и
 * десктопная версия — так же корректно проставляет cookie NEXT_LOCALE и
 * префикс пути.
 */
export function MobileLocaleSwitcher({ locale }: { locale: string }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <div
      className="relative sm:hidden"
      ref={ref}
      style={{ fontFamily: 'var(--font-landing-mono), monospace' }}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 px-2 py-1.5 rounded-lg border-hairline border-ink-200 text-[12.5px] font-semibold text-ink-700"
        aria-label="Language"
        aria-expanded={open}
      >
        <Globe size={14} strokeWidth={1.8} />
        {localeLabel(locale)}
        <ChevronDown
          size={12}
          strokeWidth={2}
          className={open ? 'rotate-180 transition-transform' : 'transition-transform'}
        />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 min-w-[110px] rounded-lg border-hairline border-ink-200 bg-white shadow-md py-1 z-50">
          {LOCALES.map(l => (
            <Link
              key={l}
              href="/"
              locale={l}
              onClick={() => setOpen(false)}
              className={
                'block px-3 py-1.5 text-[13px] transition-colors ' +
                (l === locale
                  ? 'text-ink-900 font-bold bg-ink-50'
                  : 'text-ink-600 font-medium hover:bg-ink-50')
              }
            >
              {localeLabel(l)}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
